/**
 * Full-Screen FOIA Redaction Editor Modal
 * Immersive modal overlay with drag-to-resize redaction boundaries
 */

import React, { useState, useRef, useEffect } from 'react';
import { 
  XMarkIcon,
  EyeSlashIcon, 
  CheckIcon, 
  XMarkIcon as RejectIcon,
  EyeIcon,
  DocumentTextIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  UserIcon,
  Bars3Icon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import ExemptionSelector from './ExemptionSelector';

const FullScreenRedactionEditor = ({
  initialText = '',
  initialRedactions = [],
  onRedactionsChange = () => {},
  onSave = () => {},
  onClose = () => {},
  showApprovalWorkflow = false
}) => {
  const [text, setText] = useState(initialText);
  const [redactions, setRedactions] = useState(initialRedactions);
  const [selectedRedactionIndex, setSelectedRedactionIndex] = useState(null);
  const [pdfPreviewMode, setPdfPreviewMode] = useState(false);
  const [currentWorkflowIndex, setCurrentWorkflowIndex] = useState(0);
  const [dragStartX, setDragStartX] = useState(null);
  const [dragRedactionIndex, setDragRedactionIndex] = useState(null);
  const [dragSide, setDragSide] = useState(null); // 'left' or 'right'
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const textContainerRef = useRef(null);

  // Update parent when redactions change
  useEffect(() => {
    onRedactionsChange(redactions);
  }, [redactions, onRedactionsChange]);

  // Handle escape key to close
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Create highlighted text with drag handles
  const createInteractiveText = () => {
    if (redactions.length === 0) return text;

    const sortedRedactions = [...redactions].sort((a, b) => a.start_pos - b.start_pos);
    let result = '';
    let lastPos = 0;

    sortedRedactions.forEach((redaction, index) => {
      const startPos = redaction.start_pos || 0;
      const endPos = redaction.end_pos || startPos + (redaction.original_text || '').length;

      // Add text before redaction
      result += escapeHtml(text.slice(lastPos, startPos));

      // Add redacted text with drag handles
      const redactedText = text.slice(startPos, endPos);
      const statusClass = `redaction-${redaction.status || 'pending'}`;
      const exemptionClass = `exemption-${(redaction.exemption || '(b)(1)').replace(/[()]/g, '').toLowerCase()}`;
      
      if (pdfPreviewMode) {
        // PDF preview - black bars
        result += `<span class="redaction pdf-redaction" data-index="${index}">
          ${'█'.repeat(Math.max(3, Math.floor(redactedText.length / 1.5)))}
        </span>`;
      } else {
        // Interactive mode with drag handles
        result += `
          <span class="redaction-container" data-index="${index}">
            <span 
              class="drag-handle drag-left" 
              data-index="${index}" 
              data-side="left"
              title="Drag to adjust start boundary"
            >◀</span>
            <span 
              class="redaction ${statusClass} ${exemptionClass} ${selectedRedactionIndex === index ? 'active' : ''}" 
              data-index="${index}"
              data-exemption="${redaction.exemption || '(b)(1)'}"
              data-confidence="${Math.round((redaction.confidence || 0) * 100)}"
              onclick="window.selectRedaction(${index})"
              title="${redaction.exemption || '(b)(1)'} - ${redaction.status || 'pending'} (${Math.round((redaction.confidence || 0) * 100)}%)"
            >${escapeHtml(redactedText)}</span>
            <span 
              class="drag-handle drag-right" 
              data-index="${index}" 
              data-side="right"
              title="Drag to adjust end boundary"
            >▶</span>
          </span>
        `;
      }

      lastPos = endPos;
    });

    // Add remaining text
    result += escapeHtml(text.slice(lastPos));
    return result;
  };

  const escapeHtml = (text) => {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  };

  // Handle text selection for new redactions
  const handleTextSelection = () => {
    const selection = window.getSelection();
    if (!selection.rangeCount) return;

    const range = selection.getRangeAt(0);
    const container = textContainerRef.current;
    if (!container || !container.contains(range.commonAncestorContainer)) return;

    // Calculate text positions
    const preCaretRange = range.cloneRange();
    preCaretRange.selectNodeContents(container);
    preCaretRange.setEnd(range.startContainer, range.startOffset);
    const start = preCaretRange.toString().length;

    const selectedText = range.toString();
    if (!selectedText.trim()) return;

    return {
      start_pos: start,
      end_pos: start + selectedText.length,
      original_text: selectedText
    };
  };

  // Add new redaction
  const addRedaction = (exemption = '(b)(1)') => {
    const selection = handleTextSelection();
    if (!selection) return;

    const newRedaction = {
      id: `redaction-${Date.now()}`,
      ...selection,
      exemption,
      status: 'pending',
      confidence: 0.9, // High confidence for manual redactions
      justification: 'Manually created redaction'
    };

    setRedactions(prev => [...prev, newRedaction]);
    setSelectedRedactionIndex(redactions.length);
    
    // Clear selection
    window.getSelection().removeAllRanges();
  };

  // Update redaction
  const updateRedaction = (index, updates) => {
    setRedactions(prev => prev.map((r, i) => i === index ? { ...r, ...updates } : r));
  };

  // Remove redaction
  const removeRedaction = (index) => {
    setRedactions(prev => prev.filter((_, i) => i !== index));
    setSelectedRedactionIndex(null);
  };

  // Handle drag events for boundary adjustment
  const handleMouseDown = (e) => {
    const handle = e.target.closest('.drag-handle');
    if (!handle) return;

    const index = parseInt(handle.dataset.index);
    const side = handle.dataset.side;
    
    setDragStartX(e.clientX);
    setDragRedactionIndex(index);
    setDragSide(side);
    
    e.preventDefault();
  };

  const handleMouseMove = (e) => {
    if (dragRedactionIndex === null || dragStartX === null) return;

    const deltaX = e.clientX - dragStartX;
    const charWidth = 8; // Approximate character width
    const charDelta = Math.round(deltaX / charWidth);

    if (Math.abs(charDelta) > 0) {
      const redaction = redactions[dragRedactionIndex];
      let newStartPos = redaction.start_pos;
      let newEndPos = redaction.end_pos;

      if (dragSide === 'left') {
        newStartPos = Math.max(0, Math.min(newStartPos + charDelta, newEndPos - 1));
      } else {
        newEndPos = Math.min(text.length, Math.max(newEndPos + charDelta, newStartPos + 1));
      }

      // Update the redaction with new boundaries
      const newOriginalText = text.slice(newStartPos, newEndPos);
      updateRedaction(dragRedactionIndex, {
        start_pos: newStartPos,
        end_pos: newEndPos,
        original_text: newOriginalText
      });

      setDragStartX(e.clientX);
    }
  };

  const handleMouseUp = () => {
    setDragStartX(null);
    setDragRedactionIndex(null);
    setDragSide(null);
  };

  // Attach global mouse events
  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragStartX, dragRedactionIndex, dragSide]);

  // Global function for clicking redactions
  useEffect(() => {
    window.selectRedaction = (index) => {
      setSelectedRedactionIndex(index);
      setSidebarOpen(true); // Ensure sidebar is open when selecting
    };
    return () => {
      delete window.selectRedaction;
    };
  }, []);

  // Workflow navigation
  const goToNextRedaction = () => {
    if (currentWorkflowIndex < redactions.length - 1) {
      setCurrentWorkflowIndex(currentWorkflowIndex + 1);
      setSelectedRedactionIndex(currentWorkflowIndex + 1);
    }
  };

  const goToPreviousRedaction = () => {
    if (currentWorkflowIndex > 0) {
      setCurrentWorkflowIndex(currentWorkflowIndex - 1);
      setSelectedRedactionIndex(currentWorkflowIndex - 1);
    }
  };

  const selectedRedaction = selectedRedactionIndex !== null ? redactions[selectedRedactionIndex] : null;
  const currentRedaction = showApprovalWorkflow ? redactions[currentWorkflowIndex] : null;
  const activeRedaction = showApprovalWorkflow ? currentRedaction : selectedRedaction;
  const activeIndex = showApprovalWorkflow ? currentWorkflowIndex : selectedRedactionIndex;

  return (
    <div className="fixed inset-0 z-50 flex bg-black bg-opacity-75 backdrop-blur-sm">
      
      {/* Main content area */}
      <div className={`flex-1 flex flex-col transition-all duration-300 ${sidebarOpen ? 'mr-96' : 'mr-0'}`}>
        
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="text-xl font-semibold text-gray-900">FOIA Redaction Editor</h1>
            <div className="text-sm text-gray-600">
              {redactions.length} redaction{redactions.length !== 1 ? 's' : ''}
            </div>
            {showApprovalWorkflow && (
              <div className="text-sm text-blue-600 font-medium">
                Step {currentWorkflowIndex + 1} of {redactions.length}
              </div>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPdfPreviewMode(!pdfPreviewMode)}
              className={`
                inline-flex items-center px-3 py-2 text-sm font-medium border rounded-md transition-colors
                ${pdfPreviewMode
                  ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }
              `}
            >
              {pdfPreviewMode ? (
                <>
                  <DocumentTextIcon className="h-4 w-4 mr-2" />
                  Edit Mode
                </>
              ) : (
                <>
                  <EyeIcon className="h-4 w-4 mr-2" />
                  PDF Preview
                </>
              )}
            </button>
            
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md"
              title={sidebarOpen ? 'Close sidebar' : 'Open sidebar'}
            >
              {sidebarOpen ? <ChevronRightIcon className="h-5 w-5" /> : <Bars3Icon className="h-5 w-5" />}
            </button>
            
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md"
              title="Close editor (Esc)"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Document area */}
        <div className="flex-1 overflow-auto bg-gray-50">
          <div className="max-w-none mx-auto p-8">
            <div 
              ref={textContainerRef}
              className={`
                bg-white rounded-lg shadow-sm border border-gray-200 p-8 min-h-[600px]
                ${pdfPreviewMode ? 'font-serif text-sm leading-relaxed' : 'font-sans text-base leading-relaxed'}
                cursor-text select-text
              `}
              onMouseDown={handleMouseDown}
              dangerouslySetInnerHTML={{ __html: createInteractiveText() }}
            />
            
            {/* Selection instructions */}
            {!pdfPreviewMode && !showApprovalWorkflow && (
              <div className="mt-4 text-center">
                <p className="text-sm text-gray-600 mb-2">
                  Select text in the document above, then click a button below to create a redaction:
                </p>
                <div className="flex justify-center gap-2">
                  {['(b)(1)', '(b)(6)', '(b)(7)'].map(exemption => (
                    <button
                      key={exemption}
                      onClick={() => addRedaction(exemption)}
                      className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                    >
                      Redact as {exemption}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Workflow footer */}
        {showApprovalWorkflow && redactions.length > 0 && (
          <div className="bg-white border-t border-gray-200 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={goToPreviousRedaction}
                  disabled={currentWorkflowIndex === 0}
                  className="
                    inline-flex items-center px-3 py-2 text-sm font-medium
                    bg-gray-100 text-gray-700 border border-gray-300 rounded-md
                    hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed
                  "
                >
                  <ArrowLeftIcon className="h-4 w-4 mr-1" />
                  Previous
                </button>
                
                <div className="w-64 bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all"
                    style={{ width: `${((currentWorkflowIndex + 1) / redactions.length) * 100}%` }}
                  />
                </div>
                
                <button
                  onClick={goToNextRedaction}
                  disabled={currentWorkflowIndex >= redactions.length - 1}
                  className="
                    inline-flex items-center px-3 py-2 text-sm font-medium
                    bg-gray-100 text-gray-700 border border-gray-300 rounded-md
                    hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed
                  "
                >
                  Next
                  <ArrowRightIcon className="h-4 w-4 ml-1" />
                </button>
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => updateRedaction(currentWorkflowIndex, { status: 'approved' })}
                  className="
                    inline-flex items-center px-4 py-2 text-sm font-medium
                    bg-green-600 text-white border border-green-600 rounded-md
                    hover:bg-green-700 transition-colors
                  "
                >
                  <CheckIcon className="h-4 w-4 mr-1" />
                  Approve
                </button>
                
                <button
                  onClick={() => updateRedaction(currentWorkflowIndex, { status: 'rejected' })}
                  className="
                    inline-flex items-center px-4 py-2 text-sm font-medium
                    bg-red-600 text-white border border-red-600 rounded-md
                    hover:bg-red-700 transition-colors
                  "
                >
                  <RejectIcon className="h-4 w-4 mr-1" />
                  Reject
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Sidebar */}
      {sidebarOpen && (
        <div className="w-96 bg-white border-l border-gray-200 flex flex-col">
          
          {/* Sidebar header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                {activeRedaction ? 'Redaction Details' : 'All Redactions'}
              </h2>
              <button
                onClick={() => setSidebarOpen(false)}
                className="p-1 text-gray-400 hover:text-gray-600"
              >
                <ChevronRightIcon className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Sidebar content */}
          <div className="flex-1 overflow-auto">
            
            {/* Active redaction details */}
            {activeRedaction && (
              <div className="p-6 border-b border-gray-200">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Selected Text
                    </label>
                    <div className="p-3 bg-gray-50 border rounded text-sm">
                      "{activeRedaction.original_text}"
                    </div>
                  </div>
                  
                  <ExemptionSelector
                    exemption={activeRedaction.exemption}
                    subExemption={activeRedaction.subExemption}
                    onExemptionChange={(exemption) => updateRedaction(activeIndex, { exemption })}
                    onSubExemptionChange={(subExemption) => updateRedaction(activeIndex, { subExemption })}
                    showDescription={true}
                  />
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <select
                      value={activeRedaction.status || 'pending'}
                      onChange={(e) => updateRedaction(activeIndex, { status: e.target.value })}
                      className="w-full p-2 border border-gray-300 rounded"
                    >
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                      <option value="modified">Modified</option>
                    </select>
                  </div>
                  
                  {activeRedaction.confidence && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        AI Confidence
                      </label>
                      <div className="flex items-center gap-2">
                        <div className="text-lg font-semibold">
                          {Math.round(activeRedaction.confidence * 100)}%
                        </div>
                        <div className="flex-1 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-blue-500 h-2 rounded-full"
                            style={{ width: `${activeRedaction.confidence * 100}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex gap-2 pt-2">
                    <button
                      onClick={() => removeRedaction(activeIndex)}
                      className="
                        flex-1 px-3 py-2 text-sm font-medium
                        bg-red-100 text-red-700 border border-red-200 rounded
                        hover:bg-red-200 transition-colors
                      "
                    >
                      Remove
                    </button>
                    <button
                      onClick={() => onSave(redactions)}
                      className="
                        flex-1 px-3 py-2 text-sm font-medium
                        bg-green-600 text-white border border-green-600 rounded
                        hover:bg-green-700 transition-colors
                      "
                    >
                      Save All
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Redaction list */}
            {redactions.length > 0 && !showApprovalWorkflow && (
              <div className="p-6">
                <div className="space-y-3">
                  {redactions.map((redaction, index) => (
                    <div
                      key={redaction.id || index}
                      onClick={() => setSelectedRedactionIndex(index)}
                      className={`
                        p-3 border rounded cursor-pointer text-sm transition-all
                        ${selectedRedactionIndex === index 
                          ? 'border-blue-500 bg-blue-50 shadow-sm' 
                          : 'border-gray-200 hover:bg-gray-50'
                        }
                      `}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-blue-900">
                          {redaction.exemption || '(b)(1)'}
                        </span>
                        <span className={`
                          px-2 py-1 text-xs font-medium rounded-full
                          ${redaction.status === 'approved' ? 'bg-green-100 text-green-800' :
                            redaction.status === 'rejected' ? 'bg-red-100 text-red-800' :
                            'bg-yellow-100 text-yellow-800'
                          }
                        `}>
                          {redaction.status || 'pending'}
                        </span>
                      </div>
                      <div className="text-gray-600 line-clamp-2">
                        "{redaction.original_text}"
                      </div>
                      {redaction.confidence && (
                        <div className="text-xs text-gray-500 mt-1">
                          Confidence: {Math.round(redaction.confidence * 100)}%
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {redactions.length === 0 && (
              <div className="p-6 text-center text-gray-500">
                <UserIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p>No redactions yet.</p>
                <p className="text-sm">Select text in the document to create redactions.</p>
              </div>
            )}
          </div>
        </div>
      )}

      <style jsx>{`
        .redaction-container {
          position: relative;
          display: inline-block;
        }
        
        .drag-handle {
          position: absolute;
          top: -2px;
          width: 12px;
          height: calc(100% + 4px);
          background: rgba(59, 130, 246, 0.3);
          color: rgba(59, 130, 246, 0.8);
          cursor: ew-resize;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 8px;
          opacity: 0;
          transition: opacity 0.2s;
          z-index: 10;
        }
        
        .drag-left {
          left: -6px;
        }
        
        .drag-right {
          right: -6px;
        }
        
        .redaction-container:hover .drag-handle {
          opacity: 1;
        }
        
        .redaction {
          position: relative;
          padding: 2px 4px;
          border-radius: 3px;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .redaction-pending {
          background-color: rgba(251, 191, 36, 0.3);
          border: 1px solid rgba(251, 191, 36, 0.5);
        }
        
        .redaction-approved {
          background-color: rgba(16, 185, 129, 0.3);
          border: 1px solid rgba(16, 185, 129, 0.5);
        }
        
        .redaction-rejected {
          background-color: rgba(239, 68, 68, 0.3);
          border: 1px solid rgba(239, 68, 68, 0.5);
          text-decoration: line-through;
        }
        
        .redaction.active {
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.7);
          transform: scale(1.02);
        }
        
        .pdf-redaction {
          background-color: #000000 !important;
          color: #000000 !important;
          border: none !important;
          user-select: none;
        }
        
        .exemption-b1 { border-left: 3px solid #dc2626; }
        .exemption-b6 { border-left: 3px solid #7c3aed; }
        .exemption-b7 { border-left: 3px solid #c2410c; }
        
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default FullScreenRedactionEditor;